import React, { useCallback, useEffect, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import ProductLisTr from "./ProductLisTr";
import {
  balance,
  billDiscountAmount,
  discount,
  netTotal,
  pay,
  tax,
  total,
} from "../../../store/pos/posSelector";
import {
  addItem,
  increaseItemQuantity,
  posChange,
  posInputFocus,
  posSearch,
  resetPos,
  clearSaleDate,
  setCommonDiscountType,
  setDefaultTaxPercentage,
  toggleDefaultTax,
} from "../../../store/pos/posSlice";
import baseUrl from "../../../api/baseUrl";
import { toast } from "react-toastify";
import ReceiptNew from "../../print/ReceiptNew";
import KotReceipt from "../../print/KotReceipt";
import { getDecryptedToken } from "../../../utils/crypto/cryptoUtils";
import { getCurrencySymbol } from "../../../utils/currency/currencyUtils";
import {
  setTable,
  addTableOrderItem,
  saveCurrentTableOrder,
  setTableOrderStatus,
  setOrderNumber,
  setKotDetails,
} from "../../../store/tableOrder/tableOrderSlice";
import ModelEmpty from "../../../components/commonModal/ModelEmpty";
import DeliveryDetailsModal, {
  DeliveryDetails,
} from "./popups/deliverydetails/DeliveryDetailsModal";

function ProductList({
  keys,
  saleType,
  customerId,
  editBillNumber,
  setEditBillNumber,
  editOrderNumber,
  editOrderItems,
  itemsRef,
}: any) {
  const dispatch = useDispatch();
  const printRef = useRef<HTMLDivElement>(null);
  const receiptRef = useRef<any>(null);
  const kotPrintRef = useRef<HTMLDivElement>(null);
  const kotReceiptRef = useRef<any>(null);
  const resetInitiatedRef = useRef(false); // Track if reset was initiated

  const {
    addedItemArray,
    card,
    cash,
    bank,
    commonDiscount,
    commonDiscountType,
    customerId: customerName,
  } = useSelector((state: RootState) => state.pos.current);
  const { allItems, barcode, saleDate } = useSelector(
    (state: RootState) => state.pos,
  );
  const totalAmount: number = useSelector(total);
  const discountAmount: number = useSelector(discount);
  const taxAmount: number = useSelector(tax);
  const netTotalAmount: number = useSelector(netTotal);
  const billDiscount: number = useSelector(billDiscountAmount);
  const lastbalance: number = useSelector(balance);
  const totalPay: number = useSelector(pay);

  const currencySymbol = getCurrencySymbol();

  // Remove updateOrder/finishBill options, always use normal POS options
  const [orderType, setOrderType] = useState<
    "walkin" | "takeaway" | "dinein" | "delivery"
  >("walkin");

  const defaultTaxEnabled: boolean = useSelector(
    (state: RootState) => state.pos.defaultTaxEnabled,
  );
  const defaultTaxPercentage: number | null = useSelector(
    (state: RootState) => state.pos.defaultTaxPercentage,
  );

  // On mount, read defaultTax from localStorage and push into Redux
  useEffect(() => {
    const stored = getDecryptedToken("defaultTax");
    const parsed = stored && stored !== "" ? parseFloat(stored) : null;
    dispatch(setDefaultTaxPercentage(parsed));
  }, [dispatch]);
  console.log(addedItemArray);

  // State for product dropdown
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const [selectedDropdownIndex, setSelectedDropdownIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const checkoutTableContainerRef = useRef<HTMLDivElement>(null);
  const previousItemCountRef = useRef(0);

  const resetToNeutralSale = useCallback(() => {
    dispatch(resetPos());
    if (setEditBillNumber) {
      setEditBillNumber(null);
    }
  }, [dispatch, setEditBillNumber]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    dispatch(posChange({ name, value }));
  };

  const handleBillDiscountMode = (mode: "amount" | "percent") => {
    dispatch(setCommonDiscountType(mode));
  };

  const handleBillDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (commonDiscountType === "percent") {
      const numeric = Number(inputValue || 0);
      if (inputValue !== "" && numeric > 100) {
        dispatch(posChange({ name: "commonDiscount", value: "100" }));
        return;
      }

      dispatch(posChange({ name: "commonDiscount", value: inputValue }));
      return;
    }

    dispatch(posChange({ name: "commonDiscount", value: inputValue }));
  };

  const [printData, setPrintData] = useState<any>();
  const [kotPrintData, setKotPrintData] = useState<any>(null);

  // Print receipt and reset POS after print dialog completes
  useEffect(() => {
    if (printData && receiptRef.current && !resetInitiatedRef.current) {
      // Mark that we've initiated the reset for this print
      resetInitiatedRef.current = true;

      // Step 1: Open print dialog with receipt
      receiptRef.current.print();

      // Step 2: Reset POS after print window closes
      // (1 second is enough for print dialog to open/close)
      setTimeout(() => {
        console.log("Resetting POS after print..."); // Debug log
        resetToNeutralSale(); // Clear all items/state and exit edit mode
        setIsProcessingSale(false);
        setPrintData(null); // Clear print data AFTER reset
        resetInitiatedRef.current = false; // Reset the flag for next print
        // Re-focus input for next sale
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }, 1000);
    }
  }, [printData, dispatch, resetToNeutralSale]);

  // Add useEffect for KOT printing
  useEffect(() => {
    if (kotPrintData) {
      handlePrintKot();
    }
  }, [kotPrintData]);

  // const handlePrint = useCallback(async () => {
  //   try {
  //     setPrintData(current);
  //   } catch (error) {
  //     console.log(error);
  //   }
  // }, []);

  const handlePrintKot = async () => {
    if (kotReceiptRef.current) {
      // Call the KotReceipt print method via ref
      kotReceiptRef.current.print();
      // Clear kotPrintData
      setKotPrintData(null);
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [editOrderNumber]);

  // Filter products when barcode input changes
  useEffect(() => {
    if (barcode.trim().length > 0) {
      const searchTerm = barcode.trim().toLowerCase();

      // Custom filtering with priority:
      // 1. Exact item number match
      // 2. Item number starts with search term
      // 3. Item number contains search term
      // 4. Name contains search term
      const allMatches = allItems.filter((item: any) => {
        const itemNumber = item.itemNumber?.toLowerCase() || "";
        const itemName = item.name?.toLowerCase() || "";
        return itemNumber.includes(searchTerm) || itemName.includes(searchTerm);
      });

      // Sort results by priority
      const sorted = allMatches.sort((a: any, b: any) => {
        const aItemNum = a.itemNumber?.toLowerCase() || "";
        const bItemNum = b.itemNumber?.toLowerCase() || "";

        // Exact match first
        if (aItemNum === searchTerm && bItemNum !== searchTerm) return -1;
        if (bItemNum === searchTerm && aItemNum !== searchTerm) return 1;

        // Starts with search term
        if (aItemNum.startsWith(searchTerm) && !bItemNum.startsWith(searchTerm))
          return -1;
        if (bItemNum.startsWith(searchTerm) && !aItemNum.startsWith(searchTerm))
          return 1;

        // Shorter item numbers come first (more specific)
        if (aItemNum.includes(searchTerm) && bItemNum.includes(searchTerm)) {
          return aItemNum.length - bItemNum.length;
        }

        return 0;
      });

      setFilteredProducts(sorted.slice(0, 8)); // Limit to 8 results for performance
      setShowProductDropdown(sorted.length > 0);
    } else {
      setFilteredProducts([]);
      setShowProductDropdown(false);
    }
    setSelectedDropdownIndex(-1);
  }, [barcode, allItems]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowProductDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showProductDropdown && filteredProducts.length > 0) {
      // Handle arrow navigation in dropdown
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedDropdownIndex((prev) =>
          prev < filteredProducts.length - 1 ? prev + 1 : prev,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedDropdownIndex((prev) => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (selectedDropdownIndex >= 0) {
          // Select item from dropdown
          const selectedItem = filteredProducts[selectedDropdownIndex];
          handleProductSelect(selectedItem);
        } else {
          // If no dropdown selection, use first match (which is now prioritized)
          if (filteredProducts.length > 0) {
            handleProductSelect(filteredProducts[0]);
          } else {
            toast("No item found for this barcode");
          }
        }

        // Reset search after selection
        dispatch(posSearch({ name: "barcode", value: "" }));
        setShowProductDropdown(false);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setShowProductDropdown(false);
      }
    } else if (e.key === "Enter") {
      // Original behavior for Enter key when dropdown is not visible
      const trimmed = barcode.trim().toLowerCase();

      // Try exact item number match first
      let matchedItem = allItems.find(
        (item: any) => item.itemNumber?.toLowerCase() === trimmed,
      );

      // If no exact match, find the best match using same priority logic
      if (!matchedItem) {
        const matches = allItems.filter((item: any) => {
          const itemNumber = item.itemNumber?.toLowerCase() || "";
          return itemNumber.includes(trimmed);
        });

        if (matches.length > 0) {
          // Sort and take the best match
          matchedItem = matches.sort((a: any, b: any) => {
            const aItemNum = a.itemNumber?.toLowerCase() || "";
            const bItemNum = b.itemNumber?.toLowerCase() || "";

            if (aItemNum.startsWith(trimmed) && !bItemNum.startsWith(trimmed))
              return -1;
            if (bItemNum.startsWith(trimmed) && !aItemNum.startsWith(trimmed))
              return 1;

            return aItemNum.length - bItemNum.length;
          })[0];
        }
      }

      if (matchedItem) {
        handleProductSelect(matchedItem);
      } else {
        toast("No item found for this barcode");
      }

      dispatch(posSearch({ name: "barcode", value: "" }));
    }

    // Re-focus input after processing
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleProductSelect = (product: any) => {
    const existingItem = addedItemArray.find(
      (i: any) => i.itemId === product.id,
    );
    if (existingItem) {
      dispatch(increaseItemQuantity({ itemId: product.id, existingItem }));
    } else {
      dispatch(addItem(product));
    }
  };

  const handleBarcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatch(posSearch({ name, value }));
  };

  // Add loading state for proceed button
  const [isProcessingSale, setIsProcessingSale] = useState(false);

  useEffect(() => {
    const currentCount = addedItemArray?.length || 0;
    const previousCount = previousItemCountRef.current;

    if (currentCount > previousCount && checkoutTableContainerRef.current) {
      checkoutTableContainerRef.current.scrollTo({
        top: checkoutTableContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }

    previousItemCountRef.current = currentCount;
  }, [addedItemArray]);

  const proceed = useCallback(async (
    overrideDeliveryDetails?: DeliveryDetails,
    overrideDeliveryOrderNumber?: string,
    // When true, this is an automatic "Send Order (Pay Later)" delivery
    // registration — always a zero-payment credit sale, regardless of
    // whatever saleType/cash/card/bank happen to be sitting in the checkout
    // panel. Settlement happens later via the normal credit-sale screens.
    forceCreditZeroPayment?: boolean,
    // Payment entered directly in the Delivery Details modal — used when
    // "Pay Now" is chosen but nothing was already entered in the main
    // checkout panel. Takes priority over the panel's cash/card/bank state.
    paymentOverride?: { cash: number; card: number; bank: number },
  ) => {
    if (addedItemArray.length === 0) {
      toast.error("Please add items to proceed");
      return;
    }

    // Delivery details/orderNumber come fresh from the capture modal,
    // passed in explicitly when placing a new delivery order
    const activeDeliveryDetails: DeliveryDetails | null =
      overrideDeliveryDetails || null;
    const activeDeliveryOrderNumber = overrideDeliveryOrderNumber || null;

    const effectiveCash = paymentOverride ? paymentOverride.cash : cash;
    const effectiveCard = paymentOverride ? paymentOverride.card : card;
    const effectiveBank = paymentOverride ? paymentOverride.bank : bank;
    const effectivePay = paymentOverride
      ? paymentOverride.cash + paymentOverride.card + paymentOverride.bank + billDiscount
      : totalPay;
    const effectiveBalance = paymentOverride
      ? effectivePay - netTotalAmount
      : lastbalance;

    if (!forceCreditZeroPayment) {
      // For credit sales, customer must be selected
      if (saleType === "credit" && !customerId) {
        toast.error("Please select a customer for credit sale");
        return;
      }

      // Different validation for cash vs credit sales
      if (saleType === "cash") {
        // Cash sale: require payment details
        if (
          billDiscount <= 0 &&
          (effectiveCard === "" || effectiveCard === 0) &&
          (effectiveCash === "" || effectiveCash === 0) &&
          (effectiveBank === "" || effectiveBank === 0)
        ) {
          toast.error("Please enter payment details");
          return;
        }

        // Cash sale: payment must cover the total
        if (netTotalAmount - effectivePay > 0) {
          toast.error(
            "Payment amount must be greater than or equal to net total",
          );
          return;
        }
      }
      // Credit sale: no payment validation needed, can have negative balance
    }

    const drawerId = getDecryptedToken("selectedDrawer");

    // Set loading state to true
    setIsProcessingSale(true);

    // Record start time for minimum 200ms delay
    const startTime = Date.now();

    // Show loading toast
    const loadingToastId = toast.loading("Processing sale...", {
      position: "top-right",
      closeOnClick: true,
    });

    // If editing a dine-in order, mark previous order as completed.
    // Delivery orders skip this — their lifecycle (Pending -> Delivered) is
    // tracked independently of payment and only changes via the explicit
    // "Mark Delivered" action.
    if (editOrderNumber && !activeDeliveryDetails) {
      try {
        await baseUrl.put(
          `/branch/order/order-number/${editOrderNumber}/status`,
          { status: "Completed" },
        );
      } catch (err) {
        // Optionally show error, but allow sale to continue
        toast.error("Failed to update previous order status");
      }
    }

    // Tag the invoice with which checkout flow created it, for the Sales
    // page's order-type filter. Delivery takes priority (explicit override);
    // otherwise editOrderNumber means this is closing out a dine-in table
    // order (the "dinein" selector resets back to "walkin" right after the
    // KOT is sent, so it can't be relied on by the time payment happens).
    const effectiveOrderType = activeDeliveryDetails
      ? "delivery"
      : editOrderNumber
        ? "dinein"
        : orderType;

    const salePayload = forceCreditZeroPayment
      ? {
          productData: addedItemArray,
          card: 0,
          cash: 0,
          bank: 0,
          balance: 0,
          discount: discountAmount,
          givenDiscount: billDiscount,
          tax: taxAmount,
          total: totalAmount,
          netTotal: netTotalAmount,
          drawerId: Number(drawerId),
          customerId: activeDeliveryDetails?.customerId || null,
          saleType: "credit",
          creditSale: true,
          saleDate: null,
          orderType: effectiveOrderType,
        }
      : {
          productData: addedItemArray,
          card: effectiveCard,
          cash: effectiveCash,
          bank: effectiveBank,
          balance: effectiveBalance,
          discount: discountAmount,
          givenDiscount: billDiscount,
          tax: taxAmount,
          total: totalAmount,
          netTotal: netTotalAmount,
          drawerId: Number(drawerId),
          customerId: activeDeliveryDetails?.customerId || customerId || null,
          saleType: saleType,
          creditSale: saleType === "credit", // Add creditSale boolean
          saleDate: saleDate || null, // ISO string for backdated sale, null = current time
          orderType: effectiveOrderType,
        };

    const saleRequest = editBillNumber
      ? baseUrl.put(`/branch/pos/update-sale/${editBillNumber}`, salePayload)
      : baseUrl.post("/branch/pos/create-sale", salePayload);

    saleRequest
      .then(async (response) => {
        // Calculate elapsed time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, 200 - elapsedTime);

        // Wait for minimum 200ms
        if (remainingTime > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }

        // Dismiss loading toast
        toast.dismiss(loadingToastId);

        // Show success toast with sale type info
        const saleTypeText =
          saleType === "credit" ? "Credit Sale" : "Cash Sale";
        const customerInfo = customerId
          ? `${saleTypeText} for ${customerName}`
          : `${saleTypeText} for Walk-in Customer`;

        // Show balance info for credit sales
        if (saleType === "credit" && lastbalance < 0) {
          toast.success(
            `${customerInfo} completed successfully! Credit amount: ${currencySymbol}${Math.abs(
              lastbalance,
            ).toFixed(2)}`,
            {
              position: "top-right",
              autoClose: 2000,
              pauseOnHover: false,
              closeOnClick: true,
            },
          );
        } else {
          toast.success(`${customerInfo} completed successfully!`, {
            position: "top-right",
            autoClose: 2000,
            pauseOnHover: false,
            closeOnClick: true,
          });
        }

        dispatch(
          posChange({
            name: "billNumber",
            value: response?.data?.data?.billNumber,
          }),
        );

        // Link the paid invoice back to its delivery order (kitchen/delivery
        // lifecycle status is tracked separately and is untouched by this)
        if (activeDeliveryOrderNumber && response?.data?.data?.billNumber) {
          baseUrl
            .put(
              `/branch/delivery-order/order-number/${activeDeliveryOrderNumber}/bill-number`,
              { billNumber: response.data.data.billNumber },
            )
            .catch(() => {
              // Non-fatal — payment already succeeded
            });
        }

        dispatch(clearSaleDate()); // Reset backdated mode after sale
        if (editBillNumber && setEditBillNumber) {
          setEditBillNumber(null);
        }
        setOrderType("walkin"); // Always default back to Walk In after successful sale
        setPrintData(response?.data?.data?.billNumber);

        // Refresh items to update stock and sales count
        if (itemsRef?.current?.refreshItems) {
          itemsRef.current.refreshItems();
        }
        // POS reset is now handled by the printData useEffect after print completes
      })
      .catch(async (error) => {
        // Calculate elapsed time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, 200 - elapsedTime);

        // Wait for minimum 200ms even on error
        if (remainingTime > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }

        // Dismiss loading toast
        toast.dismiss(loadingToastId);
        // Set loading state to false
        setIsProcessingSale(false);

        console.error("Sale creation error:", error);

        // Handle different types of errors
        const errorMessage = error.response?.data?.message;

        if (errorMessage) {
          // Check for specific cash drawer errors
          if (errorMessage.includes("Insufficient cash in drawer")) {
            toast.error(`💰 ${errorMessage}`, {
              position: "top-right",
              autoClose: 8000,
              pauseOnHover: false,
              closeOnClick: true,
              style: {
                backgroundColor: "#fff3cd",
                color: "#856404",
                border: "1px solid #ffeaa7",
              },
            });
          } else if (errorMessage.includes("POS drawer is not open")) {
            toast.error(`🔒 ${errorMessage}`, {
              position: "top-right",
              autoClose: 8000,
              pauseOnHover: false,
              closeOnClick: true,
              style: {
                backgroundColor: "#f8d7da",
                color: "#721c24",
                border: "1px solid #f5c6cb",
              },
            });
          } else if (
            errorMessage.includes("out of stock") ||
            errorMessage.includes("Insufficient stock")
          ) {
            toast.error(`📦 ${errorMessage}`, {
              position: "top-right",
              autoClose: 6000,
              pauseOnHover: false,
              closeOnClick: true,
              style: {
                backgroundColor: "#fff3cd",
                color: "#856404",
                border: "1px solid #ffeaa7",
              },
            });
          } else {
            toast.error(errorMessage, {
              position: "top-right",
              autoClose: 5000,
              pauseOnHover: false,
              closeOnClick: true,
            });
          }
        } else if (error.response?.status === 400) {
          toast.error("Invalid sale data. Please check your inputs.", {
            position: "top-right",
            autoClose: 5000,
            pauseOnHover: false,
            closeOnClick: true,
          });
        } else if (error.response?.status === 403) {
          toast.error("Unauthorized. Please login again.", {
            position: "top-right",
            autoClose: 5000,
            pauseOnHover: false,
            closeOnClick: true,
          });
        } else if (error.response?.status === 500) {
          toast.error("Server error. Please try again later.", {
            position: "top-right",
            autoClose: 5000,
            pauseOnHover: false,
            closeOnClick: true,
          });
        } else if (error.code === "NETWORK_ERROR" || !error.response) {
          toast.error("Network error. Please check your connection.", {
            position: "top-right",
            autoClose: 5000,
            pauseOnHover: false,
            closeOnClick: true,
          });
        } else {
          toast.error("Failed to process sale. Please try again.", {
            position: "top-right",
            autoClose: 5000,
            pauseOnHover: false,
            closeOnClick: true,
          });
        }

        // Re-focus input after error
        setTimeout(() => {
          inputRef.current?.focus();
        }, 500);
      });
  }, [
    addedItemArray,
    card,
    cash,
    bank,
    billDiscount,
    totalAmount,
    discountAmount,
    taxAmount,
    netTotalAmount,
    lastbalance,
    totalPay,
    dispatch,
    customerId,
    customerName,
    saleType,
    currencySymbol,
    editOrderNumber,
    saleDate,
    itemsRef,
    editBillNumber,
    setEditBillNumber,
    orderType,
  ]);

  // Table popup state
  const [showTablePopup, setShowTablePopup] = useState(false);
  const [tableList, setTableList] = useState<any[]>([]);
  const [, setSelectedTable] = useState<any>(null);
  // State for editing table number
  const [, setEditingTableNumber] = useState<number | null>(null);

  // Add state to track if editing an order
  const [isEditingOrder, setIsEditingOrder] = useState(
    !!editOrderNumber && !!editOrderItems && editOrderItems.length > 0,
  );

  useEffect(() => {
    setIsEditingOrder(
      !!editOrderNumber && !!editOrderItems && editOrderItems.length > 0,
    );
  }, [editOrderNumber, editOrderItems]);

  console.log(isEditingOrder, editOrderItems);

  // For edit: show only the selected table in the popup when clicking Dine In
  useEffect(() => {
    if (
      showTablePopup &&
      isEditingOrder &&
      editOrderItems &&
      editOrderItems.length > 0
    ) {
      // Only show the table from editOrderItems (editing mode)
      const table = {
        id: editOrderItems[0]?.tableId,
        tableNumber: editOrderItems[0]?.tableNumber,
        person: editOrderItems[0]?.peopleCount,
        status: "Ordered",
      };
      setTableList([table]);
    } else if (showTablePopup && !isEditingOrder) {
      // First time dine-in: show all tables
      baseUrl
        .get("/branch/table/get-tables")
        .then((res) => setTableList(res.data.data || []))
        .catch(() => setTableList([]));
    }
  }, [showTablePopup, isEditingOrder, editOrderItems]);

  // Delivery details capture — separate flow from dine-in table selection
  const [showDeliveryDetailsModal, setShowDeliveryDetailsModal] =
    useState(false);

  const handleProceed = () => {
    if (orderType === "dinein") {
      setShowTablePopup(true);
      setEditingTableNumber(null);

      // IMPORTANT: Reset editing state when opening new dine-in order
      // This ensures new orders are created, not updating previous ones
      if (!isEditingOrder) {
        // Clear any stale edit state
        setIsEditingOrder(false);
      }

      // After opening dine in popup, reset orderType to "walkin"
      setTimeout(() => {
        setOrderType("walkin");
      }, 1000);
      return;
    }
    if (orderType === "delivery" && !isEditingOrder) {
      if (addedItemArray.length === 0) {
        toast.error("Please add items to proceed");
        return;
      }
      setShowDeliveryDetailsModal(true);
      return;
    }
    proceed();
    setEditingTableNumber(null);
    setIsEditingOrder(false);
  };

  const [showTableOrderModal, setShowTableOrderModal] = useState(false);
  const [tableOrderModalData, setTableOrderModalData] = useState<any>(null);

  // Handle table selection in popup
  const handleTableSelect = (table: any) => {
    setSelectedTable(table);
    setShowTablePopup(false);
    setTableOrderModalData(table);
    setShowTableOrderModal(true);
  };

  // Add state for KOT popup
  const [showKotPopup, setShowKotPopup] = useState(false);
  const [kotPopupData] = useState<any>(null);

  // Send KOT: assign POS items to table order, create backend order, and handle KOT navigation
  const handleSendKot = async () => {
    if (!tableOrderModalData) return;

    // Set loading state for KOT
    setIsProcessingSale(true);

    // Record start time for minimum 200ms delay
    const startTime = Date.now();

    // Store items before dispatching (to avoid reading empty array after reset)
    const itemsToSend = [...addedItemArray];

    dispatch(
      setTable({
        tableId: tableOrderModalData.id,
        tableNumber: tableOrderModalData.tableNumber,
        peopleCount: tableOrderModalData.person,
      }),
    );
    itemsToSend.forEach((item: any) => {
      dispatch(
        addTableOrderItem({
          ...item,
          price: item.price,
          discount: item.discount,
          priceHistoryId: item.priceHistoryId,
          quantity: item.quantity || 1,
        }),
      );
    });
    dispatch(setTableOrderStatus("Ordered"));
    dispatch(saveCurrentTableOrder());

    // FIXED: Only include orderNumber when actually editing an existing order
    // Check if we're editing by verifying both editOrderNumber exists AND isEditingOrder is true
    const formData = {
      orders: itemsToSend.map((item: any) => ({
        itemId: item.itemId,
        quantity: item.quantity,
        orderType: "table",
        addonsId: item.addonsId,
        note: item.note,
      })),
      tableId: tableOrderModalData.id,
      peopleCount: tableOrderModalData.person,
      waiterId: 1,
      // IMPORTANT: Only pass orderNumber when explicitly editing an existing order
      ...(isEditingOrder && editOrderNumber
        ? { orderNumber: editOrderNumber }
        : {}),
      // IMPORTANT: newOrder should be true ONLY when creating a brand new order
      newOrder: !isEditingOrder,
    };

    try {
      const res = await baseUrl.post("/branch/order/create-order", formData);

      // Calculate elapsed time
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 200 - elapsedTime);

      // Wait for minimum 200ms
      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      const orderNumber = res.data?.data?.orderNumber;
      dispatch(setOrderNumber(orderNumber));
      dispatch(
        setKotDetails({
          orderNumber,
          table: tableOrderModalData,
          items: itemsToSend,
          status: "Ordered",
        }),
      );

      // Set KOT print data AFTER getting order number
      setKotPrintData({
        orderNumber,
        tableNumber: tableOrderModalData.tableNumber,
        peopleCount: tableOrderModalData.person,
        items: itemsToSend,
      });

      setShowTableOrderModal(false);

      // Show appropriate success message
      if (isEditingOrder) {
        toast.success(`Order ${orderNumber} updated successfully!`);
      } else {
        toast.success(`New order ${orderNumber} created successfully!`);
      }

      // IMPORTANT: Reset editing state after successful operation
      setIsEditingOrder(false);

      // Reset POS after a short delay to allow KOT print to complete
      setTimeout(() => {
        resetToNeutralSale();
      }, 500);

      // Wait for Redux state to clear
      const checkArrayCleared = setInterval(() => {
        const currentItems = addedItemArray;
        if (!currentItems || currentItems.length === 0) {
          clearInterval(checkArrayCleared);
          setIsProcessingSale(false);
        }
      }, 100);

      // Fallback: clear after 3 seconds
      setTimeout(() => {
        clearInterval(checkArrayCleared);
        setIsProcessingSale(false);
      }, 3000);
    } catch (err) {
      // Calculate elapsed time
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 200 - elapsedTime);

      // Wait for minimum 200ms even on error
      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      console.error("Create order error:", err);
      toast.error("Failed to send order to table");
      setIsProcessingSale(false);
      setKotPrintData(null); // Clear on error
    }
  };

  // Create a new delivery order in its own dedicated DeliveryOrders table,
  // separate from the dine-in table-order flow. If payNow is true, also
  // proceed straight to payment for the same cart right after.
  const handleSendDeliveryKot = async (
    details: DeliveryDetails,
    payNow: boolean,
    paymentOverride?: { cash: number; card: number; bank: number },
  ) => {
    if (addedItemArray.length === 0) {
      toast.error("Please add items to proceed");
      return;
    }

    setIsProcessingSale(true);
    const startTime = Date.now();
    const itemsToSend = [...addedItemArray];

    const formData = {
      orders: itemsToSend.map((item: any) => ({
        itemId: item.itemId,
        quantity: item.quantity,
        addonsId: item.addonsId,
        note: item.note,
      })),
      deliveryPhoneNumber: details.deliveryPhoneNumber,
      deliveryAddress: details.deliveryAddress || undefined,
      deliveryNote: details.deliveryNote || undefined,
      customerId: details.customerId || undefined,
    };

    try {
      const res = await baseUrl.post(
        "/branch/delivery-order/create-order",
        formData,
      );

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 200 - elapsedTime);
      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      const orderNumber = res.data?.data?.orderNumber;
      toast.success(`Delivery order ${orderNumber} created successfully!`);
      setShowDeliveryDetailsModal(false);

      if (payNow) {
        // Pay immediately for the same cart — kitchen still sees the order
        // via the DeliveryOrders row just created above; this creates the
        // invoice and links it back to that order.
        await proceed(details, orderNumber, false, paymentOverride);
      } else {
        // Pay later — register immediately as a zero-payment credit sale,
        // reusing the existing credit-sale system for settlement (Sales
        // page / Customer Info), instead of inventing a new payable state.
        await proceed(details, orderNumber, true);
      }
    } catch (err) {
      console.error("Create delivery order error:", err);
      toast.error("Failed to create delivery order");
      setIsProcessingSale(false);
    }
  };

  // Table selection popup for Dine In
  return (
    <>
      <div className="checkout-container !rounded-none !shadow-none">
        <div className="mb-1 flex items-center justify-between px-0.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8ea3b8]">
            Billing
          </span>
          <span className="rounded bg-[#eef3f8] px-1.5 py-0.5 text-[10px] font-semibold text-erp-muted">
            Tab → cart
          </span>
        </div>
        <div className="search-and-order-row">
          <div className="searching" style={{ position: "relative", flex: 1 }}>
            <input
              key={editOrderNumber || "default"}
              className="searching"
              ref={inputRef}
              type="text"
              name="barcode"
              value={barcode}
              onChange={handleBarcodeChange}
              onKeyDown={handleKeyDown}
              onFocus={(e) => {
                dispatch(
                  posInputFocus({ name: "common", value: e.target.name }),
                );
              }}
              placeholder="Scan barcode or search product..."
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />

            {/* Product dropdown */}
            {showProductDropdown && filteredProducts.length > 0 && (
              <div
                ref={dropdownRef}
                className="product-dropdown"
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  width: "100%",
                  maxHeight: "300px",
                  overflowY: "auto",
                  backgroundColor: "white",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  zIndex: 1000,
                }}
              >
                {filteredProducts.map((product, index) => (
                  <div
                    key={product.id}
                    onClick={() => {
                      handleProductSelect(product);
                      dispatch(posSearch({ name: "barcode", value: "" }));
                      setShowProductDropdown(false);
                      inputRef.current?.focus();
                    }}
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #eee",
                      cursor: "pointer",
                      backgroundColor:
                        selectedDropdownIndex === index ? "#f0f0f0" : "white",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: "bold" }}>{product.name}</div>
                      <div style={{ fontSize: "0.8em", color: "#666" }}>
                        {product.itemNumber}
                      </div>
                    </div>
                    <div>
                      <div style={{ textAlign: "right" }}>
                        {currencySymbol} {product.price}
                      </div>
                      <div
                        style={{
                          fontSize: "0.8em",
                          color:
                            Number(product.stock) >
                            Number(product.alertQuantity)
                              ? "green"
                              : "orange",
                        }}
                      >
                        Stock: {product.stock}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <select
            value={orderType}
            onChange={(e) =>
              setOrderType(
                e.target.value as "walkin" | "takeaway" | "dinein" | "delivery",
              )
            }
            className="order-type-select"
          >
            <option value="walkin">Walk In</option>
            <option value="takeaway">Takeaway</option>
            <option value="dinein">Dine In</option>
            <option value="delivery">Delivery</option>
          </select>
        </div>

        <div
          className="checkout-table-container"
          ref={checkoutTableContainerRef}
        >
          <table className="checkout-table">
            <thead>
              <tr>
                <th style={{ width: 44 }}></th>
                <th style={{ textAlign: "left", width: 148 }}>Name</th>
                <th style={{ width: 90 }}>QTY</th>
                <th style={{ width: 48 }}>Tax</th>
                <th style={{ width: 62 }}>Price</th>
                <th style={{ width: 46 }}>Disc</th>
                <th style={{ width: 62 }}>S.Total</th>
                <th style={{ width: 34 }}></th>
              </tr>
            </thead>
            <tbody>
              {addedItemArray?.map((item: any, i: number) => (
                <ProductLisTr item={item} key={i} />
              ))}
            </tbody>
          </table>
        </div>

        <div className="summary">
          <div className="summary-panel summary-panel--billing">
            <h4 className="summary-title">Billing Summary</h4>

            <div className="summary-row">
              <span className="summary-label">Subtotal</span>
              <span className="summary-value">
                {currencySymbol} {totalAmount.toFixed(2)}
              </span>
            </div>

            <div className="summary-row">
              <span className="summary-label">Product Discount</span>
              <span className="summary-value">
                {currencySymbol} {discountAmount.toFixed(2)}
              </span>
            </div>

            <div className="summary-row">
              <span className="summary-label">Bill Discount</span>
              <span className="summary-value">
                {currencySymbol} {billDiscount.toFixed(2)}
              </span>
            </div>

            <div className="summary-row">
              <span
                className="summary-label"
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                Tax
                {defaultTaxPercentage !== null && (
                  <button
                    type="button"
                    onClick={() => dispatch(toggleDefaultTax())}
                    title={
                      defaultTaxEnabled
                        ? `Default tax ${defaultTaxPercentage}% ON - click to disable`
                        : `Apply default tax ${defaultTaxPercentage}% to untaxed items`
                    }
                    style={{
                      fontSize: "10px",
                      padding: "1px 5px",
                      borderRadius: "10px",
                      border: "1px solid",
                      cursor: "pointer",
                      fontWeight: 600,
                      lineHeight: 1.4,
                      backgroundColor: defaultTaxEnabled
                        ? "#16a34a"
                        : "#e5e7eb",
                      color: defaultTaxEnabled ? "#fff" : "#374151",
                      borderColor: defaultTaxEnabled ? "#15803d" : "#d1d5db",
                      transition: "all 0.15s",
                    }}
                  >
                    {defaultTaxEnabled
                      ? `${defaultTaxPercentage}% ON`
                      : `${defaultTaxPercentage}% OFF`}
                  </button>
                )}
              </span>
              <span className="summary-value">
                {currencySymbol} {taxAmount.toFixed(2)}
              </span>
            </div>

            <div className="summary-row summary-row--net">
              <span className="summary-label">Net Total</span>
              <span className="summary-value summary-value--net">
                {currencySymbol} {(netTotalAmount - billDiscount).toFixed(2)}
              </span>
            </div>
          </div>

          <div className="summary-panel summary-panel--payment">
            <h4 className="summary-title">Payment Method</h4>

            <div className="payment-list">
              <label className="payment-row">
                <span className="payment-label">Cash</span>
                <input
                  type="text"
                  className="payment-input"
                  placeholder={`${currencySymbol} 0.00`}
                  onChange={handleChange}
                  name="cash"
                  value={cash}
                  ref={keys}
                  onFocus={(e) =>
                    dispatch(
                      posInputFocus({ name: "common", value: e.target.name }),
                    )
                  }
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </label>

              <label className="payment-row">
                <span className="payment-label">Card</span>
                <input
                  type="text"
                  className="payment-input"
                  placeholder={`${currencySymbol} 0.00`}
                  onChange={handleChange}
                  name="card"
                  value={card}
                  onFocus={(e) =>
                    dispatch(
                      posInputFocus({ name: "common", value: e.target.name }),
                    )
                  }
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </label>

              <label className="payment-row">
                <span className="payment-label">Bank</span>
                <input
                  type="text"
                  className="payment-input"
                  placeholder={`${currencySymbol} 0.00`}
                  onChange={handleChange}
                  name="bank"
                  value={bank}
                  onFocus={(e) =>
                    dispatch(
                      posInputFocus({ name: "common", value: e.target.name }),
                    )
                  }
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </label>

              <label className="payment-row payment-row--discount">
                <span className="payment-label payment-label--with-toggle">
                  Bill Discount
                  <span className="discount-mode-toggle">
                    <button
                      type="button"
                      className={`discount-mode-btn ${commonDiscountType === "amount" ? "active" : ""}`}
                      onClick={() => handleBillDiscountMode("amount")}
                    >
                      {currencySymbol}
                    </button>
                    <button
                      type="button"
                      className={`discount-mode-btn ${commonDiscountType === "percent" ? "active" : ""}`}
                      onClick={() => handleBillDiscountMode("percent")}
                    >
                      %
                    </button>
                  </span>
                </span>
                <input
                  type="text"
                  className="payment-input"
                  placeholder={
                    commonDiscountType === "percent"
                      ? `Percent (e.g. 10)`
                      : `Amount (e.g. ${currencySymbol} 100)`
                  }
                  onChange={handleBillDiscountChange}
                  name="commonDiscount"
                  value={commonDiscount}
                  onFocus={(e) =>
                    dispatch(
                      posInputFocus({ name: "common", value: e.target.name }),
                    )
                  }
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="checkout-footer">
          <div className="balance-row">
            <span className="balance-label">
              Items: {addedItemArray?.length || 0}
            </span>
            <span className="balance-right">
              <span className="balance-label">
                {saleType === "credit" && customerId ? "Credit" : "Balance"}:
              </span>
              <span
                className={`balance-amount ${lastbalance < 0 && saleType === "credit" ? "credit-amount" : ""}`}
              >
                {currencySymbol}{" "}
                {saleType === "credit" && customerId
                  ? Number(lastbalance).toFixed(2)
                  : lastbalance < 0
                    ? "0.00"
                    : Number(lastbalance).toFixed(2)}
              </span>
            </span>
          </div>
          <div className="footer-buttons">
            <button
              className="empty-cart-btn"
              onClick={resetToNeutralSale}
              disabled={isProcessingSale}
            >
              Empty Cart
            </button>
            <button
              className={`proceed-btn ${customerId && saleType === "credit" ? "credit-sale" : ""}`}
              onClick={handleProceed}
              disabled={isProcessingSale}
              style={{
                opacity: isProcessingSale ? 0.6 : 1,
                cursor: isProcessingSale ? "not-allowed" : "pointer",
              }}
            >
              {isProcessingSale
                ? "Processing..."
                : orderType === "dinein"
                  ? "Dine In"
                  : orderType === "delivery"
                    ? "Delivery"
                    : customerId && saleType === "credit"
                      ? "Credit Sale"
                      : "PAY"}
            </button>
          </div>
        </div>
      </div>
      <div className="ebillll">
        <div ref={printRef} style={{ display: "none" }}>
          <ReceiptNew ref={receiptRef} />
        </div>
        {/* Add KOT print container */}
        <div ref={kotPrintRef} style={{ display: "none" }}>
          {kotPrintData && (
            <KotReceipt
              ref={kotReceiptRef}
              orderNumber={kotPrintData.orderNumber}
              tableNumber={kotPrintData.tableNumber}
              peopleCount={kotPrintData.peopleCount}
              items={kotPrintData.items}
            />
          )}
        </div>
      </div>
      <style>{`
        /* Remove toggle button styles since dropdown is used */
      `}</style>

      {/* Table selection popup for Dine In */}
      {showTablePopup && (
        <div className="pos-table-popup-bg">
          <div className="pos-table-popup">
            {/* NEW: X Close Button in top-right corner */}
            <button
              className="pos-table-popup-close-x"
              onClick={() => setShowTablePopup(false)}
              aria-label="Close"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <h3 className="pos-table-popup-title">Select Table</h3>

            {/* Apply dynamic grid class based on table count */}
            <div
              className={`table ${
                tableList.length >= 5 ? "table-grid-3" : "table-grid-2"
              }`}
            >
              {tableList.map((table: any) => (
                <button
                  key={table.id}
                  className={`status_button pos-table-btn`}
                  style={{
                    backgroundColor:
                      (table.status === "Available" && "#1f6f8b") ||
                      (table.status === "Not Ordered" && "#D7D7D7") ||
                      (table.status === "Ordered" && "#F2A91C") ||
                      (table.status === "Payment Initiated" && "#067904") ||
                      (table.status === "Uncleaned" && "#DC1818") ||
                      (table.status === "Reserved" && "#3FC091") ||
                      "#D7D7D7",
                  }}
                  onClick={() => handleTableSelect(table)}
                >
                  <div className="table_card">
                    <div className="table_info">
                      <div className="table_number_time">
                        <span className="table_number">
                          {table.tableNumber}
                        </span>
                      </div>
                      <div className="table_people">
                        <span className="people_count">
                          {table.person} Persons
                        </span>
                      </div>
                      <div className="table_status">{table.status}</div>
                    </div>
                  </div>
                  <div className="arrow">
                    <svg
                      width="18"
                      height="18"
                      fill="#1f6f8b"
                      viewBox="0 0 24 24"
                    >
                      <path d="M10 17l5-5-5-5v10z" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
            <button
              className="pos-table-popup-close"
              onClick={() => setShowTablePopup(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Table Order Modal after selecting table */}
      {showTableOrderModal && tableOrderModalData && (
        <div className="pos-table-popup-bg">
          <div className="pos-table-popup">
            <h3 className="pos-table-popup-title">
              Table #{tableOrderModalData.tableNumber}
            </h3>
            <div style={{ marginBottom: 12, color: "#666", fontWeight: 500 }}>
              Persons: {tableOrderModalData.person}
            </div>
            <div style={{ marginBottom: 18 }}>
              <table className="checkout-table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>QTY</th>
                    <th>Price</th>
                    <th>Discount</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {addedItemArray.map((item: any, i: number) => (
                    <tr key={i}>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>
                        {currencySymbol} {item.price}
                      </td>
                      <td>{item.discount}</td>
                      <td>
                        {currencySymbol}{" "}
                        {(
                          item.price * item.quantity -
                          (item.discount * item.quantity || 0)
                        ).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Wrap buttons in modal-actions container for side-by-side */}
            <div className="modal-actions">
              <button
                className="proceed-btn"
                onClick={handleSendKot}
                disabled={addedItemArray.length === 0 || isProcessingSale}
              >
                {isProcessingSale ? "Processing..." : "Send KOT"}
              </button>
              <button
                className="pos-table-popup-close"
                onClick={() => setShowTableOrderModal(false)}
                disabled={isProcessingSale}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KOT Popup after sending order */}
      {showKotPopup && kotPopupData && (
        <ModelEmpty
          setopen={setShowKotPopup}
          open={showKotPopup}
          className="kot-popup"
        >
          <div style={{ padding: 24, minWidth: 320 }}>
            <h3 style={{ marginBottom: 8 }}>
              Table #{kotPopupData.table?.tableNumber}
            </h3>
            <div style={{ marginBottom: 8 }}>
              <strong>Order Number:</strong> {kotPopupData.orderNumber}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Status:</strong> {kotPopupData.status}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>Persons:</strong> {kotPopupData.table?.person}
            </div>
            <div>
              <table className="checkout-table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>QTY</th>
                    <th>Price</th>
                    <th>Discount</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {kotPopupData.items.map((item: any, i: number) => (
                    <tr key={i}>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>
                        {currencySymbol} {item.price}
                      </td>
                      <td>{item.discount}</td>
                      <td>
                        {currencySymbol}{" "}
                        {(
                          item.price * item.quantity -
                          (item.discount * item.quantity || 0)
                        ).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              className="pos-table-popup-close"
              style={{ marginTop: 18, width: 120 }}
              onClick={() => setShowKotPopup(false)}
            >
              Close
            </button>
          </div>
        </ModelEmpty>
      )}

      <DeliveryDetailsModal
        open={showDeliveryDetailsModal}
        setOpen={setShowDeliveryDetailsModal}
        onConfirm={(details, payNow, paymentOverride) =>
          handleSendDeliveryKot(details, payNow, paymentOverride)
        }
        currentCash={Number(cash || 0)}
        currentCard={Number(card || 0)}
        currentBank={Number(bank || 0)}
        currencySymbol={currencySymbol}
        netTotal={netTotalAmount}
      />

      {/* Update Order Table Popup (only show the current table) */}
      {/* {showUpdateOrderTablePopup && (
        <div className="pos-table-popup-bg">
          <div className="pos-table-popup">
            <h3 className="pos-table-popup-title">Update Table Order</h3>
            <div className="table">
              <button
                className="status_button pos-table-btn"
                style={{
                  backgroundColor: "#F2A91C",
                }}
                onClick={handleUpdateOrderTableSelect}
              >
                <div className="table_card">
                  <div className="table_info">
                    <div className="table_number_time">
                      <span className="table_number">
                        {editOrderItems[0]?.tableNumber}
                      </span>
                    </div>
                    <div className="table_people">
                      <span className="people_count">
                        {editOrderItems[0]?.peopleCount} Persons
                      </span>
                    </div>
                    <div className="table_status">Ordered</div>
                  </div>
                </div>
                <div className="arrow">
                  <svg
                    width="18"
                    height="18"
                    fill="#1f6f8b"
                    viewBox="0 0 24 24"
                  >
                    <path d="M10 17l5-5-5-5v10z" />
                  </svg>
                </div>
              </button>
            </div>
            <button
              className="pos-table-popup-close"
              onClick={() => setShowUpdateOrderTablePopup(false)}
            >
              Close
            </button>
          </div>
        </div>
      )} */}
    </>
  );
}

export default ProductList;
